/**
 * BaseAdapter is an abstract base class for AI adapters.
 * It defines the common interface that all adapters should implement.
 * 
 * @property {string} name - The name of the adapter.
 * @property {string} model - The model used by the AI.
 * @property {string} endpoint - The endpoint of the AI service.
 * @property {string} apiKey - The API key for the AI service.
 * @property {boolean} asStream - Whether the AI service should be used as a stream.
 * @property {Object} config - The configuration object for the AI service.
 * 
 * @throws {Error} When trying to instantiate the abstract BaseAdapter class directly.
 */

import type { AdapterConfig, AdapterParams, Chat, InternalMessage } from "@/utils/ai-adapters/xdc/types";
import type { AudioResultSimpleJson } from "@azure/openai";

class BaseAdapter {
    name
    model
    endpoint
    apiKey
    asStream
    config: AdapterConfig | undefined

    constructor(name = 'BaseAdapter', config: AdapterConfig | undefined = undefined) {
        if (new.target === BaseAdapter)
            throw new Error("Cannot instantiate abstract class")

        this.name = name
        this.model = config?.model
        this.endpoint = config?.endpoint
        this.apiKey = config?.key
        this.asStream = config?.asStream
        this.config = config
    }

    /**
     * Initializes the adapter.
     *
     * @param {Function} updateChat - The function to update the chat.
     * 
     * @throws {Error} When not implemented by a subclass.
     */
    init(updateChat?: (messageList: InternalMessage[]) => Promise<void>): Promise<void>
    async init(): Promise<void> {
        throw new Error(`Method 'init' must be implemented`);
    }

    /**
     * Gets chat completions from the AI service.
     * 
     * @param {unknown[]} messageList
     * @param {string} message - The message to get completions for.
     * @param {string} role - The role of the message sender.
     * @param {unknown} params
     * 
     * @throws {Error} When not implemented by a subclass.
     */
    getChatCompletions(messageList: unknown[], message: string | FormData | Chat, role?: string, params?: unknown): Promise<void>
    async getChatCompletions(): Promise<void> {
        throw new Error(`Method 'getChatCompletions' must be implemented`)
    }

    /**
     * Converts speech to text using the AI service.
     * 
     * @param {Blob} audio - The audio to convert to text.
     * @param {userId} string
     * @param {token} string
     * 
     * @throws {Error} When not implemented by a subclass.
     */
    getAudioTranscription(audio: Blob, params: AdapterParams): Promise<AudioResultSimpleJson | undefined>
    async getAudioTranscription(): Promise<AudioResultSimpleJson | undefined> {
        throw new Error(`Method 'speechToText' must be implemented`);
    }
}

export default BaseAdapter